import clsx from "clsx";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import Slider from "react-slick";
import CloseButton from "web/react/components/buttons/close-button/close-button";
import { MoreProducts } from "web/react/components/product-gallery/product-gallery-more-products/product-gallery-more-products";
import "web/react/components/product-gallery/slick-theme.less";
import "web/react/components/product-gallery/slick.less";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { Text } from "web/react/emo/text";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import { useInStockProductPageContext } from "web/react/pages/product/in-stock/in-stock-product-page/in-stock-product-page.context";

import {
    Image,
    ProductGalleryImage,
} from "web/react/pages/product/product-image-gallery/product-gallery-image";
import { ImageChangeEventSource } from "web/react/pages/product/product-image-gallery/product-image-gallery";
import { sendAnalyticsEvent } from "web/react/pages/product/product-image-gallery/utils";
import { getSafariMajorVersion } from "web/react/utils/user-agent";
import { suppressBannerDisabled, suppressBannerEnabled } from "web/redux/ducks/top-banner";
import store from "web/redux/store";
import { gettext } from "web/script/modules/django-i18n";
import * as styles from "./product-image-gallery-fullscreen.css";
import { ThumbnailStrip } from "./thumbnail-strip";
import { ZoomableSlide, ZoomDirection } from "./zoomable-slide";

interface ProductImageGalleryFullscreenProps {
    images: Image[];
    onClose: () => void;
    productId: string;
    buyOptionId: string;
    currentSlideIndex: number;
    onSlideChange: (src: ImageChangeEventSource, nextSlide: number) => void;
    onSwipe: (direction: "left" | "right") => void;
}

interface ArrowButtonProps {
    variant: "prev" | "next";
    className?: string;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
    onSlideChange: (src: ImageChangeEventSource, nextSlide: number) => void;
    currentSlideIndex: number;
    isMobile?: boolean;
    disabled?: boolean;
}

function ArrowButton({
    variant,
    className,
    onClick,
    onKeyDown,
    onSlideChange,
    currentSlideIndex,
    isMobile = false,
    disabled = false,
}: ArrowButtonProps): React.ReactElement {
    const rotation = variant === "prev" ? 90 : -90;
    const ariaLabel =
        variant === "prev"
            ? gettext("product_image_gallery_fullscreen.previous_image")
            : gettext("product_image_gallery_fullscreen.next_image");
    const baseStyle = isMobile
        ? styles.mobileNavigationArrow
        : variant === "prev"
        ? styles.prevArrow
        : styles.nextArrow;

    const onClickWithAnalytics = (e: React.MouseEvent<HTMLButtonElement>): void => {
        onClick?.(e);
        onSlideChange(
            "arrow_click",
            variant === "prev" ? currentSlideIndex - 1 : currentSlideIndex + 1
        );
    };

    return (
        <button
            className={clsx(baseStyle, className)}
            onClick={onClickWithAnalytics}
            onKeyDown={onKeyDown}
            aria-label={ariaLabel}
            disabled={disabled}
            tabIndex={0}
        >
            <SVGIcon name="chevron-thin" rotate={rotation} className={styles.arrowIcon} />
        </button>
    );
}

function PrevArrow(props: Omit<ArrowButtonProps, "variant">): React.ReactElement {
    return <ArrowButton {...props} variant="prev" />;
}

function NextArrow(props: Omit<ArrowButtonProps, "variant">): React.ReactElement {
    return <ArrowButton {...props} variant="next" />;
}

export function ProductImageGalleryFullscreen({
    images,
    onClose,
    productId,
    buyOptionId,
    currentSlideIndex,
    onSlideChange,
    onSwipe,
}: ProductImageGalleryFullscreenProps): React.ReactElement {
    const [isImageZoomed, setIsImageZoomed] = useState(false);
    const [isTapHidden, setIsTapHidden] = useState(false);
    const isUIHidden = isImageZoomed || isTapHidden;
    const { isMobileViewport, isTabletViewport } = useDomViewport();

    const sliderRef = useRef<Slider | null>(null);

    const { relatedProducts } = useInStockProductPageContext();
    const showMoreProducts = isMobileViewport && relatedProducts && relatedProducts.length > 0;

    const isIOS18 = getSafariMajorVersion() === 18;

    useEffect(() => {
        if (isMobileViewport) {
            store.dispatch(suppressBannerEnabled());
            return () => {
                store.dispatch(suppressBannerDisabled());
            };
        }
    }, [isMobileViewport]);

    const totalSlides = images.length + (showMoreProducts ? 1 : 0);
    const canGoToPrevious = currentSlideIndex > 0;
    const canGoToNext = currentSlideIndex < totalSlides - 1;

    const currentImagePosition = currentSlideIndex + 1;

    const onImageZoom = useCallback(
        (direction: ZoomDirection) => {
            sendAnalyticsEvent(`zoomed_${direction}`, true, productId, buyOptionId);
        },
        [productId, buyOptionId]
    );

    const onImageClick = useCallback(() => {
        if ((isMobileViewport || isTabletViewport) && !isImageZoomed) {
            if (!isTapHidden) {
                sendAnalyticsEvent("removed_ui_elements", true, productId, buyOptionId);
            }
            setIsTapHidden(!isTapHidden);
        }
    }, [productId, buyOptionId, isTapHidden, isMobileViewport, isTabletViewport, isImageZoomed]);

    const handlePreviousClick = useCallback(() => {
        if (canGoToPrevious) {
            sliderRef.current?.slickPrev();
        }
    }, [canGoToPrevious]);

    const handleNextClick = useCallback(() => {
        if (canGoToNext) {
            sliderRef.current?.slickNext();
        }
    }, [canGoToNext]);

    useEffect(() => {
        sliderRef.current?.slickGoTo(currentSlideIndex, true);
    }, [currentSlideIndex]);

    const onThumbnailClick = useCallback(
        (index: number) => {
            if (index !== currentSlideIndex) {
                sliderRef.current?.slickGoTo(index);
                onSlideChange("thumbnail", index);
            }
        },
        [currentSlideIndex, onSlideChange]
    );

    return ReactDOM.createPortal(
        <div data-testid="fullscreen-gallery" className={styles.overlay} tabIndex={-1}>
            {!isUIHidden ? (
                <div className={styles.topControls}>
                    {images.length > 0 && currentSlideIndex < images.length ? (
                        <div
                            className={styles.imagePositionIndicator}
                            aria-label={gettext(
                                "product_image_gallery.invisible_text_for_accessibility.position_indicator",
                                {
                                    current_image_position: currentImagePosition,
                                    image_length: images.length,
                                }
                            )}
                        >
                            <Text as="span" textStyle="body-s">
                                {`${currentImagePosition}/${images.length}`}
                            </Text>
                        </div>
                    ) : null}
                    <span className={styles.closeButton}>
                        <CloseButton onClick={onClose} />
                    </span>
                </div>
            ) : null}
            <Slider
                ref={sliderRef}
                speed={400}
                slidesToShow={1}
                centerMode={false}
                infinite={false}
                arrows={!isUIHidden}
                swipe
                onSwipe={onSwipe}
                touchMove
                draggable={false}
                dots={false}
                beforeChange={() => {
                    setIsTapHidden(false);
                }}
                prevArrow={
                    <PrevArrow
                        onSlideChange={onSlideChange}
                        currentSlideIndex={currentSlideIndex}
                        disabled={!canGoToPrevious}
                    />
                }
                nextArrow={
                    <NextArrow
                        onSlideChange={onSlideChange}
                        currentSlideIndex={currentSlideIndex}
                        disabled={!canGoToNext}
                    />
                }
                className={styles.slider}
                initialSlide={currentSlideIndex}
            >
                {images.map((image, index) => (
                    <div key={`${image.id}-${index}`} className={styles.zoomContainer}>
                        <ZoomableSlide
                            index={index}
                            currentSlideIndex={currentSlideIndex}
                            onZoom={onImageZoom}
                            onZoomStateChange={setIsImageZoomed}
                            onClick={onImageClick}
                        >
                            <ProductGalleryImage
                                src={image.url}
                                alt={image.altText ?? undefined}
                                lazy={!isIOS18}
                                isFullscreen
                                blurImageOnLoad={!isIOS18}
                                tabIndex={-1}
                            />
                        </ZoomableSlide>
                    </div>
                ))}
                {showMoreProducts && (
                    <MoreProducts
                        key="related-slide"
                        products={relatedProducts.slice(0, 4)}
                        fullscreen
                    />
                )}
            </Slider>
            {!isUIHidden ? (
                <div className={styles.thumbnailNavigationContainer}>
                    <div className={styles.mobileThumbnailNavigation}>
                        <PrevArrow
                            onClick={handlePreviousClick}
                            onSlideChange={onSlideChange}
                            currentSlideIndex={currentSlideIndex}
                            disabled={!canGoToPrevious}
                            isMobile
                        />
                        <ThumbnailStrip
                            images={images}
                            currentSlideIndex={currentSlideIndex}
                            onThumbnailImageClick={onThumbnailClick}
                            isMobileInline
                            moreProductsImages={
                                showMoreProducts
                                    ? relatedProducts
                                          .slice(0, 4)
                                          .flatMap((p) => (p.image_url ? [p.image_url] : []))
                                    : undefined
                            }
                        />
                        <NextArrow
                            onClick={handleNextClick}
                            onSlideChange={onSlideChange}
                            currentSlideIndex={currentSlideIndex}
                            disabled={!canGoToNext}
                            isMobile
                        />
                    </div>
                    <div className={styles.desktopThumbnailNavigation}>
                        <ThumbnailStrip
                            images={images}
                            currentSlideIndex={currentSlideIndex}
                            onThumbnailImageClick={onThumbnailClick}
                        />
                    </div>
                </div>
            ) : null}
        </div>,
        document.body
    );
}
